import { store } from '@store/store'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { TaskModel } from '@store/slices/task.slice'
import { poolNewTask } from '@store/actions/task.actions'
import {
  NarrationModel,
  setNarrationInProgress,
} from '@store/slices/narration.slice'
import { generateAudio, publishNarration } from '@store/thunk/narration.thunk'
import { AudioSourceType } from '@shared/enums'
import { PromethistTranscription } from '@shared/classes'

export const isNarrationUpdated = (narration: NarrationModel): boolean =>
  !!narration &&
  !!narration.audioCreated &&
  (!narration.uploaded ||
    (!!narration.uploaded &&
      new Date(narration.uploaded) < new Date(narration.audioCreated)))

export const handleGenerateAudioForNarration = async (
  narration: NarrationModel,
  playlistId: EntityId,
  source: AudioSourceType,
  dialogueNumber?: number,
  filetype?: string,
  normalization?: boolean
): Promise<EntityId> => {
  const dispatch = store.dispatch

  return dispatch(
    generateAudio({
      narrationId: narration.id,
      source,
      filetype,
      normalization,
      dialogueNumber,
    })
  )
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Generování audia pro naraci ${narration.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${narration.aiSlot}/region/${narration.region}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [narration.id],
          },
          {
            entityName: 'aiSlot',
            entityIds: [narration.aiSlot],
          },
          {
            entityName: 'playlist',
            entityIds: [playlistId],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(narration.id))
      return taskId
    })
}

export const areTranscriptionsEqual = (
  transcriptionA: PromethistTranscription,
  transcriptionB: PromethistTranscription
): boolean => {
  // Check if the number of sessions is different
  if (transcriptionA.sessions.length !== transcriptionB.sessions.length) {
    return false
  }

  // Compare each session
  for (let i = 0; i < transcriptionA.sessions.length; i++) {
    const dialogueA = transcriptionA.sessions[i]
    const dialogueB = transcriptionB.sessions[i]

    // Check if the number of sentences in the transcript is different
    if (dialogueA.transcript.length !== dialogueB.transcript.length) {
      return false
    }

    // Compare each sentence in the transcript
    for (let j = 0; j < dialogueA.transcript.length; j++) {
      const sentenceA = dialogueA.transcript[j]
      const sentenceB = dialogueB.transcript[j]

      if (
        sentenceA.personaName !== sentenceB.personaName ||
        sentenceA.transcript !== sentenceB.transcript
      ) {
        return false
      }
    }
  }

  return true
}

export const handlePublishNarration = async (
  narration: NarrationModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const dispatch = store.dispatch

  return dispatch(
    publishNarration({
      narrationId: narration.id,
    })
  )
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Publikování narace ${narration.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${narration.aiSlot}/region/${narration.region}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [narration.id],
          },
          {
            entityName: 'aiSlot',
            entityIds: [narration.aiSlot],
          },
          {
            entityName: 'playlist',
            entityIds: [playlistId],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(narration.id))
      return taskId
    })
}
